import { createAsyncThunk } from "@reduxjs/toolkit"
import apiRequest from "../../lib/http"
import { extractApiError, normalizeError } from "../../utils/errorHelpers"

// ═══════════════════════════════════════════════════════════════════════════════════════════
// WS 1406230 — the public onboarding page can now present a scoped, expiring link token (`?t=`).
//
// When it does, the two public operations go to the token-authenticated route variants
// (`/bank-account-link/:token/anonymous`), which resolve the contact FROM THE TOKEN and take no
// userId input at all. When it does not, they fall to the legacy `:userId` routes — the grace
// path the backend deliberately keeps alive so invitations already in clients' inboxes do not
// 404 mid-onboarding. See vlg-server!1221.
//
// Both thunks still accept a bare userId string, because the AUTHENTICATED CRM caller
// (components/PaymentMethodTab/AddPaymentMethodModal.jsx) has no link token and never will.
// ═══════════════════════════════════════════════════════════════════════════════════════════

const resolveLinkTarget = (arg) => (
  arg && typeof arg === 'object'
    ? { userId: arg.userId, token: arg.token || null }
    : { userId: arg, token: null }
)

const bankAccountLinkPath = ({ userId, token }) => (
  token ? `/bank-account-link/${token}/anonymous` : `/bank-account-link/${userId}`
)

/**
 * Reject with the STATUS as well as the message.
 *
 * A refused token comes back 403 carrying copy written for the client ("This link has expired.
 * Please ask your representative to send a new one."), but the API's error handler labels every
 * such response `name: 'Server error'` — so the status is the only reliable signal that the LINK
 * was refused rather than the request having merely failed. The page needs to tell those apart to
 * show an expired-link state instead of a generic error.
 */
// `normalizeError` is handed the whole axios error on purpose: that branch carries both the
// Forbidden shape ({ name: 'Server error', errors: '<message string>' }) and the guard that
// refuses to show a raw 5xx body to a client. A bare transport error has no API body at all, so
// it gets the caller's fallback rather than "Network Error".
const rejectLinkError = (error, fallback) => ({
  message: error?.response?.data?.error ? normalizeError(error) : fallback,
  status: error?.response?.status || null,
})

export const createBankAccountLink = createAsyncThunk('bankAccountLink/createBankAccountLink', async (userId, { rejectWithValue }) => {
  try {
    const response = await apiRequest.post(`/bank-account-link/${userId}`)
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data?.error.errors[0].message || 'Failed to create custom user'
    return rejectWithValue(errorMessage)
  }
})

/**
 * Read the onboarding invitation for the public page.
 *
 * @param {string|{userId: string, token: ?string}} arg
 * Rejects with { message, status } — the status is what lets the page tell a refused LINK (403)
 * from a request that merely failed. No consumer reads the old plain-string rejection.
 */
export const getBankAccountLinkById = createAsyncThunk('bankAccountLink/getBankAccountLinkById', async (arg, { rejectWithValue }) => {
  try {
    const response = await apiRequest.get(bankAccountLinkPath(resolveLinkTarget(arg)))
    return response.data

  } catch (error) {
    return rejectWithValue(rejectLinkError(error, 'Failed to fetch customer'))
  }
})

/**
 * Retire the onboarding invitation once a payment method has been saved.
 *
 * @param {string|{userId: string, token: ?string}} arg
 * No body is sent on either variant: the tokened route takes the contact from the token, and
 * a body userId that disagreed with it would be refused with a 403 by design.
 */
export const editBankAccountLinkById = createAsyncThunk('bankAccountLink/editBankAccountLinkById', async (arg, { rejectWithValue }) => {
  try {
    const response = await apiRequest.put(bankAccountLinkPath(resolveLinkTarget(arg)))
    return response.data

  } catch (error) {
    return rejectWithValue(rejectLinkError(error, 'Failed to fetch customer'))
  }
})

export const changePrimaryPaymentMethod = createAsyncThunk('bankAccountLink/editBankAccountLinkById', async ({ userId, billingAccountId }, { rejectWithValue }) => {
  try {
    const response = await apiRequest.put(`/billing-accounts/${billingAccountId}/users/${userId}/primary`)
    return response.data

  } catch (error) {
    const errorMessage = error.response?.data?.error.errors[0].message || 'Failed to fetch customer'
    return rejectWithValue(errorMessage)
  }
})

export const getBillingInfo = createAsyncThunk('bankAccountLink/getBillingInfo', async ({ billingType, billingAccountId }, { rejectWithValue }) => {
  try {
    const response = await apiRequest.get(`/billing-accounts/${billingAccountId}/type/${billingType}`)
    return response.data

  } catch (error) {
    const errorMessage = error.response?.data?.error.errors[0].message || 'Failed to fetch customer'
    return rejectWithValue(errorMessage)
  }
})

export const changeBillingProcessorInfo = createAsyncThunk('bankAccountLink/getBillingInfo', async ({ processor, billingId }, { rejectWithValue }) => {
  try {
    const response = await apiRequest.put(`/billing-accounts/${billingId}/processor/${processor}`)
    return response.data

  } catch (error) {
    const errorMessage = error.response?.data?.error.errors[0].message || 'Failed to fetch customer'
    return rejectWithValue(errorMessage)
  }
})

// Auto vs Review processing mode for an account: 'review' holds the account's
// payments for the manual Daily Processing queue; 'auto' lets the 06:00 cron charge them.
export const changeProcessingMode = createAsyncThunk('bankAccountLink/changeProcessingMode', async ({ mode, billingId }, { rejectWithValue }) => {
  try {
    const response = await apiRequest.put(`/billing-accounts/${billingId}/processing-mode/${mode}`)
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data?.error?.errors?.[0]?.message || 'Failed to change processing mode'
    return rejectWithValue(errorMessage)
  }
})

export const getBillingSecrets = createAsyncThunk('bankAccountLink/getBillingSecrets', async ({ billingAccountId }, { rejectWithValue }) => {
  try {
    const response = await apiRequest.get(`/billing-accounts/${billingAccountId}/card/secrets`)
    return response.data

  } catch (error) {
    const errorMessage = error.response?.data?.error.errors[0].message || 'Failed to fetch customer'
    return rejectWithValue(errorMessage)
  }
})

export const getBankSecrets = createAsyncThunk('bankAccountLink/getBankSecrets', async ({ billingAccountId }, { rejectWithValue }) => {
  try {
    const response = await apiRequest.get(`/billing-accounts/${billingAccountId}/bank/secrets`)
    return response.data

  } catch (error) {
    const errorMessage = error.response?.data?.error.errors[0].message || 'Failed to fetch bank secrets'
    return rejectWithValue(errorMessage)
  }
})

export const updateBillingAddress = createAsyncThunk('bankAccountLink/updateBillingAddress', async ({ billingId, data }, { rejectWithValue }) => {
  try {
    const response = await apiRequest.put(`/billing-accounts/${billingId}/address`, data)
    return response.data

  } catch (error) {
    const errorMessage = error.response?.data?.error.errors[0].message || 'Failed to fetch customer'
    return rejectWithValue(errorMessage)
  }
})

export const updateCardById = createAsyncThunk('bankAccountLink/updateCardById', async ({ billingId, data }, { rejectWithValue }) => {
  try {
    const response = await apiRequest.put(`/cards/${billingId}`, data)
    return response.data

  } catch (error) {
    const errorMessage = error.response?.data?.error.errors[0].message || 'Failed to fetch customer'
    return rejectWithValue(errorMessage)
  }
})

export const updateCardSecretsById = createAsyncThunk('bankAccountLink/updateCardSecretsById', async ({ billingId, data }, { rejectWithValue }) => {
  try {
    const response = await apiRequest.put(`/cards/${billingId}/secrets`, data)
    return response.data

  } catch (error) {
    const errorMessage = error.response?.data?.error.errors[0].message || 'Failed to fetch customer'
    return rejectWithValue(errorMessage)
  }
})

export const updateBankInfo = createAsyncThunk('bankAccountLink/updateBankInfo', async ({ billingId, data }, { rejectWithValue }) => {
  try {
    const response = await apiRequest.put(`/billing-accounts/${billingId}`, data)
    return response.data

  } catch (error) {
    const errorMessage = error.response?.data?.error.errors[0].message || 'Failed to fetch customer'
    return rejectWithValue(errorMessage)
  }
})

export const updateBankSecrets = createAsyncThunk('bankAccountLink/updateBankSecrets', async ({ billingId, data }, { rejectWithValue }) => {
  try {
    const response = await apiRequest.put(`/billing-accounts/${billingId}/secrets`, data)
    return response.data

  } catch (error) {
    return rejectWithValue(extractApiError(error, 'Failed to fetch customer'))
  }
})
// Clears the NACHA unauthorized-return debit block on a bank account by recording that the
// customer has authorized us to debit it again.
//
// Not an account edit, which is why it is gated to the workspace admin while every other
// mutation in this file reaches supervisors too: it asserts what the customer said, and on the
// strength of that assertion we resume debiting an account the customer's bank told us we were
// not permitted to debit. `reference` is the evidence an ODFI audit asks for and the endpoint
// refuses the call without it.
//
// Errors go through normalizeError rather than the hand-rolled
// `error.response?.data?.error.errors[0].message` used above: the endpoint's two real refusals
// (a 403 for the wrong role, a 400 for an authorization that predates the block) do not both
// carry an `errors` array, and the operator has to be able to read which one they hit.
export const recordDebitAuthorization = createAsyncThunk('bankAccountLink/recordDebitAuthorization', async ({ billingId, reference, authorizedAt, note }, { rejectWithValue }) => {
  try {
    const response = await apiRequest.post(`/billing-accounts/${billingId}/debit-authorization`, {
      reference,
      ...(authorizedAt ? { authorizedAt } : {}),
      ...(note ? { note } : {}),
    })
    return response.data

  } catch (error) {
    return rejectWithValue(normalizeError(error))
  }
})
